import axios from "axios";
import { Action, ThunkAction } from "@reduxjs/toolkit";
import config from "@Config/index";
import { AppState, AppThunk } from "@Store/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import { getInventoriesRequestHeaders } from "./fetchInventoriesData";
import {
  recommendationsFailed,
  recommendationsPageLoaded,
  recommendationsPageLoading,
  recommendationsPlanned,
  recommendationsPlanning,
} from "./searchPageReducer";
import { tokensFromFilters } from "../utils/filterApplicationOrder";
import {
  RECOMMENDATIONS_PAGE_SIZE,
  buildRemovalOrder,
  excludesSoldOut,
  negationParams,
  relaxParams,
  withSoldOutStep,
} from "../utils/relaxation";

const COUNT_URL = `${config.API_URL}/api/v1/inventories/inventory_filter_count`;
const LIST_URL = `${config.API_URL}/api/v1/inventories`;

/** Shared event properties for every recommendation event (requirement §8). */
export const getRecommendationEventContext = (state: AppState, platform: "desktop" | "mobile") => {
  const { region, filters, recommendations } = state.searchPageData;
  return {
    category: PAGE.SEARCH,
    region_id: region?.id,
    region_type: region?.region_type,
    main_result_count: recommendations.mainResultCount,
    applied_filters: tokensFromFilters(filters),
    platform,
    build: config.IS_CHINA_APP ? "china" : "global",
  };
};

const emitFailure = (state: AppState, stage: "planning" | "initial" | "load_more", error: any) => {
  gtagEventEmitter({
    action: "search_recommendations_failed",
    ...getRecommendationEventContext(state, state.appData?.isDesktop ? "desktop" : "mobile"),
    stage,
    error_type: error?.code || error?.response?.status || error?.message || "unknown",
  });
};

/**
 * Plan the relaxation steps from counts alone (HLD D2). Runs once per exhausted filtered
 * search; requires every main result to be rendered so superset subtraction is exact.
 */
export const planRecommendations = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const { recommendations, searchList, filters, filterApplicationOrder, region } =
    state.searchPageData;
  if (recommendations.status !== "idle") return;

  // After a radius expansion `meta.count` may describe only the nearby band (university flows),
  // so the rendered list is the reliable "seen" total once it is exhausted.
  const rendered = searchList.items?.length ?? 0;
  const mainCount = state.searchPageData.isRadiusExpanded
    ? rendered
    : Number(searchList.meta?.count ?? rendered);
  // Not fully rendered yet. Only reachable unexpanded; expanded, `mainCount` is `rendered`.
  if (rendered < mainCount) return;

  const tokens = tokensFromFilters(filters);
  const removal = buildRemovalOrder(tokens, filterApplicationOrder);
  if (!removal.length) {
    dispatch(recommendationsPlanned({ plan: [], mainCount }));
    return;
  }

  const requestId = recommendations.requestId + 1;
  dispatch(recommendationsPlanning(requestId));
  const isStale = () => getState().searchPageData.recommendations.requestId !== requestId;

  try {
    const headers = getInventoriesRequestHeaders(getState, filters, region);
    const count = (removed: string[], soldOut = false) =>
      axios
        .get(COUNT_URL, {
          params: {
            ...relaxParams(searchList.params, removed, { soldOut }),
            agg_keys: "available",
            use_java_api: true,
          },
          headers,
          withCredentials: true,
        })
        .then(({ data }) => Number(data?.data?.meta?.count));
    // Relax exactly one filter: the last applied (one option for amenities / bills included).
    // Counts are available-only, so the baseline is the main filters' available count.
    const removed = removal.slice(0, 1);
    const [baseline, stepCount, soldOutCount] = await Promise.all([
      excludesSoldOut(searchList.params) ? mainCount : count([]),
      count(removed),
      count(removed, true),
    ]);
    if (isStale()) return;

    const pool = Math.max(0, stepCount - baseline) || 0;
    const plan = withSoldOutStep(
      pool
        ? [{ index: 1, removedTokens: removed, pool, sortKey: "relevance_sorting_v2" as const }]
        : [],
      removed,
      soldOutCount,
    );
    dispatch(recommendationsPlanned({ plan, mainCount }));
    if (plan.length) await dispatch(loadRecommendationsUntil(RECOMMENDATIONS_PAGE_SIZE));
  } catch (error) {
    if (isStale()) return;
    dispatch(recommendationsFailed());
    emitFailure(getState(), "planning", error);
  }
};

/** Fetch one page of the current step. Resolves `true` when it is worth calling again. */
type BooleanThunk = ThunkAction<Promise<boolean>, AppState, unknown, Action<string>>;

const fetchRecommendationsPage = (): BooleanThunk => async (dispatch, getState) => {
  const state = getState();
  const { recommendations, searchList, filters, region } = state.searchPageData;
  if (recommendations.status !== "ready" || recommendations.pageLoading) return false;

  // "ready" guarantees a step: the reducer finishes once the last step is exhausted.
  const step = recommendations.plan[recommendations.currentStepIdx];

  // Negate the last accepted step's filter set (the main filters for the first step) — HLD D13.
  // The sold-out tail step needs none: `available=false` already excludes everything shown.
  const prevAccepted = recommendations.plan[recommendations.currentStepIdx - 1];
  const notFilter =
    !step.soldOut && negationParams(searchList.params, prevAccepted?.removedTokens ?? []);

  const requestId = recommendations.requestId + 1;
  dispatch(recommendationsPageLoading(requestId));
  const isStale = () => getState().searchPageData.recommendations.requestId !== requestId;

  try {
    const { data } = await axios.get(LIST_URL, {
      params: {
        ...relaxParams(searchList.params, step.removedTokens, { soldOut: step.soldOut }),
        sort_key: step.sortKey,
        sort_order: "desc",
        limit: RECOMMENDATIONS_PAGE_SIZE,
        p: recommendations.currentPage,
        ...(notFilter && { not_filter: notFilter }),
        use_java_api: true,
      },
      headers: getInventoriesRequestHeaders(getState, filters, region),
      withCredentials: true,
    });
    if (isStale()) return false;
    dispatch(
      recommendationsPageLoaded({
        items: data?.data?.result ?? [],
        next: data?.data?.meta?.next ?? null,
        stepIndex: step.index,
      }),
    );
    return getState().searchPageData.recommendations.status === "ready";
  } catch (error) {
    if (isStale()) return false;
    dispatch(recommendationsFailed({ keepItems: recommendations.items.length > 0 }));
    emitFailure(getState(), recommendations.items.length ? "load_more" : "initial", error);
    return false;
  }
};

/**
 * Page until at least `target` recommendation cards are loaded or the pool is exhausted.
 * Crosses step boundaries so the section always fills to the requested count (FR-26/27).
 */
export const loadRecommendationsUntil =
  (target: number): AppThunk =>
  async (dispatch, getState) => {
    // Cap one fill at 20 requests: a page can add few cards (sold-out pages drop main-list
    // repeats), and a misbehaving `next` must not loop forever.
    let guard = 0;
    while (getState().searchPageData.recommendations.items.length < target && guard < 20) {
      guard += 1;
      // eslint-disable-next-line no-await-in-loop
      const more = await dispatch(fetchRecommendationsPage());
      if (!more) break;
    }
  };

/**
 * Called from `useSearchList`'s scroll handler, which already fires near the end of the list
 * (the section renders inside it): pages the section once the main list is exhausted.
 */
export const loadMoreRecommendationsIfNeed = (): AppThunk => (dispatch, getState) => {
  const { searchList, recommendations } = getState().searchPageData;
  if (searchList.next || recommendations?.status !== "ready" || recommendations.pageLoading) return;
  dispatch(loadRecommendationsUntil(recommendations.items.length + RECOMMENDATIONS_PAGE_SIZE));
};
